import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  DevError,
  HTTP_STATUS_CODE_API_CONFLICT,
  HTTP_STATUS_CODE_API_FORBIDDEN,
  HTTP_STATUS_CODE_API_NOTAUTHORIZED,
  HTTP_STATUS_CODE_API_NOTFOUND,
  HTTP_STATUS_CODE_API_TOOMANY,
  HTTP_STATUS_CODE_API_VALIDATION,
  HTTP_STATUS_CODE_GATEWAY_TIMEOUT,
  HTTP_STATUS_CODE_UNKNOWN_ERROR,
  HTTP_STATUS_CODE_WEB_VALIDATION_BASE,
  SafariInjector
} from '@safarilaw-webapp/shared/common-objects-models';
import { AppDialogUiReduxObject, ConfirmationDialogButton } from '@safarilaw-webapp/shared/dialog/store-access';
import { AppConfigurationService } from '@safarilaw-webapp/shared/environment';
import { ErrorMessageParserService, ErrorObject, FileTransferError } from '@safarilaw-webapp/shared/error-handling-message-parser';
import { LoggerService } from '@safarilaw-webapp/shared/logging';
import { AbortSave, ReduxErrorUiOption } from '@safarilaw-webapp/shared/redux';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ErrorDialogComponent } from '../../components/error-dialog/error-dialog.component';
// Generic class for handling error messages

declare function showErrorPage();

export const CHUNK_RETRY_COUNT_PARAM = '__chcn';
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService extends SafariInjector implements ErrorHandler {
  private _showingError = false;
  private _sub: Subscription;
  private _lastErrorShown: string = null;
  private _lastErrorShownTime = 0;
  // A stale-chunk deploy fails every sibling @defer block in the same idle batch, so the NG0750 branch
  // can fire several times for one incident. Latch on the first so only it drives recovery.
  private _deferRecoveryStarted = false;
  static retryChunkError(urlWithOrigin: string, throwErrorIfRetriesFail = false): number {
    const url = new URL(urlWithOrigin);

    if (url.pathname.toLowerCase() == '/error' || url.pathname.toLowerCase() == '/interstitial') {
      // Do not continue retrying on these two - chunk retry will eventually send to error
      // so we don't want to retry anymore
      return;
    }
    let chunkCount = parseInt(url.searchParams.get(CHUNK_RETRY_COUNT_PARAM), 10);
    if (isNaN(chunkCount)) {
      chunkCount = 0;
    }
    chunkCount++;
    if (chunkCount <= 3) {
      url.searchParams.set(CHUNK_RETRY_COUNT_PARAM, chunkCount.toString());

      setTimeout(() => {
        window.history.pushState({}, document.title, url.toString());

        window.location.href = url.toString();
      }, 500);
    } else if (throwErrorIfRetriesFail) {
      setTimeout(() => {
        throw new Error('App reinitialization failed.');
      });
    }
    return chunkCount;
  }

  /**
   * True when an error message indicates a failed dynamic module/chunk load — the recoverable class of
   * failure that a reload (retryChunkError) can fix, typically a client on a stale cached index.html
   * after a deploy requesting hashed chunks that no longer exist. Covers every browser wording we've
   * seen: webpack's ChunkLoadError / "Loading chunk N failed", Chrome/Edge's "failed to fetch
   * dynamically imported module", and Safari's "Importing a module script failed" / the SPA-fallback
   * "'text/html' is not a valid JavaScript MIME type" (index.html served in place of the missing .js).
   * Single source of truth so the three in-app recovery sites (this handler, app.component bootstrap,
   * error page) can't drift apart. The pre-boot inline hooks in apps/lpms/src/index.html and
   * apps/rms/src/index.html mirror this list and retryChunkError's __chcn behavior — they can't
   * import from the bundle (it's the thing that failed to load), so all sites have to move together.
   */
  static isDynamicImportFailure(message: string): boolean {
    if (!message) {
      return false;
    }
    const lower = message.toLowerCase();
    return (
      message.includes('ChunkLoadError') ||
      (lower.includes('loading chunk') && lower.includes('failed')) ||
      lower.includes('failed to fetch dynamically imported module') ||
      lower.includes('importing a module script failed') ||
      lower.includes('not a valid javascript mime type')
    );
  }

  private _injector: Injector;
  private _zone: NgZone;

  constructor() {
    super();
    this._injector = this.inject(Injector);
    this._zone = this.inject(NgZone);
  }

  navigateWithRetry(url) {
    const router = this._injector.get(Router);
    this._zone.run(() => {
      router
        .navigate([url], {
          skipLocationChange: true
        })
        .catch(() => {
          // 3163 - If angular fails when navigating to one of the error pages user will end up with a blank page
          // This will happen if OnDestroy of the page that is causing an error also caused an error. What happens is:

          // 1. Page X throws an exception somewhere - let's say in ngOnInit
          // 2. Our error handler service gets called and tries to navigate to the error page
          // 3. Navigation starts. As part of the navigation to the new page the old page is getting torn down.
          // 4. Angular invokes ngOnDestroy of PageX.
          // 5. ngOnDestroy throws another exception. THIS is the part that angular hates. The exception is being thrown while it's trying to navigate.
          // 6. Angular stops

          // To deal with this we'll give it one more final shot, by dummy-navigating (otherwise NG will do nothing),
          // then immediately back to the page we wanted to go to.
          // If even that fails... just do full browser refresh not sure there's much more we can do... ¯\_(ツ)_/¯

          // P.S. Not sure that we need setTimeout , much less 25ms , but it won't
          // hurt and might be good just to make sure that any pending promises got cleared.

          // I m also wondering if we even need interstitial page anymore, now that our reload strategy is
          // to do full reload, but leaving that alone for now
          setTimeout(() => {
            void router
              .navigateByUrl('/interstitial', { skipLocationChange: true })
              .then(o => {
                if (o) {
                  router
                    .navigate([url], {
                      skipLocationChange: true
                    })
                    .catch(() => {
                      window.location.href = '/';
                    });
                } else {
                  window.location.href = '/';
                }
              })
              .catch(() => {
                // If this happens its' probably some hard error AFTER NG boostrapped but if failed
                // because it couldn't navigate to even this final call. This could happen if there
                // was a failure in spinning up a lazy loaded module. In this case call our global error page
                showErrorPage();
              });
          }, 25);
        });
    });
  }
  handleError(thrownError: any): void {
    if (typeof thrownError == 'boolean') {
      // Don't log error of type boolean - that's thrown by initializer when reloading service worker
      return;
    }

    const errorTimestamp = DateTime.now().toMillis();
    thrownError.uuid = uuidv4();
    const location = this._injector.get(LocationStrategy);
    const url = location instanceof PathLocationStrategy ? location.path() : '';

    if (thrownError instanceof AbortSave) {
      // AbortSave is a special kind of error used for short-circuiting multi-step saves and it can
      // be thrown by form validations, dialog cancellations or API actions (even if ignore errors is set to true)
      // For ease of calling our dialogs now default to throwing abortsave on cancel by default. While the dialogs
      // that are inside rxjs flows (save/delete/etc) will have AbortSave handled internally we still have
      // a number of standalone dialogs out there that will now start raising this error on cancel. We don't
      // want to log warnings for that and there is no real harm in simply ignoring it.
      return;
    }

    const errorMessageParser = this._injector.get(ErrorMessageParserService);
    const errorObject = errorMessageParser.getErrorObjectFromThrownError(thrownError);

    // #12562 - Errors thrown entirely by third-party code (CDN scripts like Stripe, browser
    // extensions, analytics SDKs, etc.) bubble through Zone.js into here and would otherwise be
    // treated as app errors — logged as errors and routed to the hard error page (which now also
    // triggers a full refresh). They are not ours to surface. Swallow them with a low-severity
    // breadcrumb for visibility instead.
    if (this._isNonSafariError(errorObject)) {
      this._logSuppressedError('Suppressed non-Safari (third-party) error', errorObject, url, ErrorHandlerService._wrapperStack(errorObject));
      return;
    }

    // pdf.js failures escape into this handler and would otherwise route the user to the hard error
    // page. All are suppressed to an Information breadcrumb instead — the viewer bundle is served from
    // our own origin, so the non-Safari rule above can't catch them. Two families:
    //  - expected lifecycle control-flow exceptions: render cancellation (RenderingCancelledException,
    //    "startCleanup: Page N is currently rendering.") and worker/transport teardown ("Transport
    //    destroyed", "Worker was destroyed" — a pending render/getPage/cleanup rejecting after the
    //    viewer's worker was torn down, e.g. the user finalizes an SDM intake and closes the viewer
    //    while a render is still in flight).
    //  - genuine runtime errors thrown from inside the vendored pdf.js bundle itself (a wheel handler
    //    dereferencing a torn-down node, a drag handler hitting a transpile gap, a fetch range-reader
    //    aborting on teardown) — third-party pdf.js bugs, not ours to surface on the error page.
    // We deliberately still log these (Information breadcrumb) rather than dropping them fully: a pdf.js
    // major update ships with this iteration's ng update, and the breadcrumb count is how we detect
    // whether the new viewer fixes or worsens each race. Each family logs under a distinct title so it
    // stays independently countable in elmah across that bump.
    const pdfJsSuppressionTitle = this._pdfJsSuppressionTitle(thrownError, errorObject);
    if (pdfJsSuppressionTitle) {
      // Log the stack the rule classified on, not the zone wrapper's — on Firefox/Safari the wrapper
      // holds zone.js frames only, so the breadcrumb could never say WHICH handler fired.
      this._logSuppressedError(pdfJsSuppressionTitle, errorObject, url, ErrorHandlerService._bestStack(thrownError, errorObject));
      return;
    }

    // Flexmonster re-arms its row measure from an animation frame, and disposing a pivot cancels only the idle callback,
    // so leaving a report mid-render can run the measure against the disposed grid. A vendor race, not ours to surface.
    const bestStack = ErrorHandlerService._bestStack(thrownError, errorObject);
    if (ErrorHandlerService._isFlexmonsterDisposedMeasure(ErrorHandlerService._composeErrorMessages(thrownError, errorObject), bestStack)) {
      this._logSuppressedError('Suppressed Flexmonster disposed-pivot measure error', errorObject, url, bestStack);
      return;
    }

    // Angular raises NG0750 when a @defer block fails to load its lazy dependency chunk. This is the
    // same underlying failure as a ChunkLoadError (a lazy chunk that didn't fetch — typically a stale
    // cached index.html after a deploy), but Angular wraps it in a RuntimeError whose message is just
    // "NG0750", so it slips past both the ChunkLoadError detection below and the error page's chunk
    // recovery, landing the user on the hard error page with no retry. Route it through the same
    // reload-based recovery a background ChunkLoadError gets instead. Keep an Information breadcrumb so
    // we can see how often recovery fires.
    // A stale-chunk deploy typically fails several sibling @defer blocks in the same idle batch, so this
    // can fire multiple times for one incident. Only the first drives recovery — it schedules one reload
    // and logs one breadcrumb; later siblings are swallowed so they don't each log/navigate while the
    // reload is pending. NG0750 also covers a defer dependency that genuinely throws (not just a missing
    // chunk), which no reload can fix, and in prod the two are indistinguishable. So gate on
    // retryChunkError's return: it schedules a reload and returns the attempt count only while <= 3,
    // and returns undefined when it won't retry (already on /error|/interstitial). When recovery is
    // exhausted or not applicable, fall through so the real failure is logged and shown instead of
    // reloading forever silently. Pass false: the fall-through surfaces the real error, so we don't also
    // want retryChunkError's generic "App reinitialization failed." throw.
    if (this.isPlatformBrowser && this._isDeferBlockLoadError(thrownError, errorObject)) {
      if (this._deferRecoveryStarted) {
        return;
      }
      const retryAttempt = ErrorHandlerService.retryChunkError(window.location.href, false);
      if (retryAttempt != null && retryAttempt <= 3) {
        // Latch ONLY once a reload is actually scheduled, so the sibling burst is dropped while the
        // reload is pending. On the exhausted/declined path we deliberately do NOT latch: __chcn=3
        // survives the error page's location.reload(), so a later NG0750 would otherwise hit the latch
        // and be dropped forever (no log, no error page, defer sections never render). Leaving it
        // unlatched lets the failure keep surfacing; the 250ms _lastErrorShown throttle below already
        // collapses the fall-through sibling burst (they share the NG0750:false:undefined key).
        this._deferRecoveryStarted = true;
        // The reload is only scheduled (retryChunkError does setTimeout(500) then sets location.href) and
        // can still be cancelled — e.g. a dirty form's beforeunload "Leave site?" prompt that the user
        // dismisses. If that happens the page never reloads, so re-arm once the reload window has passed;
        // otherwise the latch stays true for the session and every later NG0750 is dropped (Angular never
        // re-runs a FAILED defer block, so the sections stay blank with no signal). 5s comfortably clears
        // the 500ms schedule + prompt while staying far shorter than any real inter-incident gap, so the
        // millisecond-apart sibling burst is still deduped.
        setTimeout(() => (this._deferRecoveryStarted = false), 5000);
        // Include the attempt so a first-try recovery is distinguishable from the third reload before
        // the error page — this Information breadcrumb is more reliable than the base page's own
        // Chunk Error Recovery entry, which is emitted ~500ms before location.href tears the page down.
        // The attempt goes in `data`, NOT the title: elmah groups by title, so putting it there would
        // split one recovery signal across three groups and make "how often does recovery fire" a sum
        // instead of a count. Same shape as LogChunkErrorRecoveryToElmah's `chunkRetryCount`.
        this._logSuppressedError('Recovering @defer block dependency load failure (NG0750)', errorObject, url, ErrorHandlerService._wrapperStack(errorObject), [
          { key: 'deferRetryAttempt', value: String(retryAttempt) }
        ]);
        return;
      }
    }

    let errorObjectAsString = '';
    // Use errorObject.message, rather than stringified JSON error object. If we stringify the entire object
    // it will always be different since it will contain different unique error ID, even if everything else
    // is the same. However, we should also check for source and silent options in this comparison

    errorObjectAsString = errorObject.message + ':' + (thrownError.silent != null ? (thrownError.silent as boolean).toString() : 'false') + ':' + (thrownError.source as string);
    // throttle back to back errors
    if (
      !this._lastErrorShown || // if never showed the error
      this._lastErrorShown !== errorObjectAsString || // or the error is different than the last
      errorTimestamp - this._lastErrorShownTime > 250 // or more than quarter of a second expired since the error was shown
    ) {
      this._lastErrorShown = errorObjectAsString;
      this._lastErrorShownTime = errorTimestamp;
      this.showAndLogMessage(thrownError, errorObject, url);
    }
  }

  /**
   * True when the error originated entirely in third-party code (browser extensions, CDN-hosted
   * scripts such as Stripe, analytics SDKs, etc.) rather than our own app bundle. Our app is always
   * served from our own origin, so if the error has a usable stack and NOT ONE frame is from our
   * origin, it isn't ours to surface. One structural rule covers extensions (chrome-extension://
   * etc.) AND cross-origin scripts (js.stripe.com) without a per-vendor message denylist.
   *
   * This is intentionally not 100% accurate — it won't catch errors with no/sanitized stack, or
   * third-party code that has been fully inlined under our own origin — but it reliably covers the
   * large majority of real-world cases (browser extensions and CDN-hosted scripts) with zero ongoing
   * maintenance. We err on the side of NOT suppressing when unsure, so a genuine app error is never
   * silently swallowed.
   */
  private _isNonSafariError(errorObject: ErrorObject): boolean {
    // SSR / non-browser: third-party scripts don't run there and we have no window.location to
    // compare against, so never suppress.
    if (!this.isPlatformBrowser) {
      return false;
    }
    // Never suppress an error our own API raised.
    if (errorObject?.safariApiError) {
      return false;
    }
    return ErrorHandlerService._isNonSafariStack(ErrorHandlerService._wrapperStack(errorObject), window.location.host);
  }

  /**
   * The stack of the error zone.js handed us — jsStack, or the original error's when jsStack is empty.
   * For a wrapped rejection this is the WRAPPER's stack, which on Firefox/Safari carries zone.js frames
   * only (see _pdfJsSuppressionTitle). What the non-Safari rule classifies on.
   */
  private static _wrapperStack(errorObject: ErrorObject): string {
    return (errorObject?.jsStack || errorObject?.originalError?.stack || '') as string;
  }

  /**
   * Best available stack for a possibly zone-wrapped error: the rejection's OWN stack first, then the
   * wrapper's. The pdf.js rules and their breadcrumb must BOTH resolve through here, or classification
   * and logging drift apart and the breadcrumb names frames the rule never saw. Deliberately NOT used by
   * _isNonSafariError — that rule classifies on the wrapper stack, so its breadcrumb uses _wrapperStack.
   */
  private static _bestStack(thrownError: any, errorObject: ErrorObject): string {
    return (thrownError?.rejection?.stack as string) || ErrorHandlerService._wrapperStack(errorObject);
  }

  /**
   * Pure helper: given an error stack and our own host, true when every resolvable frame is served
   * from some other host. No stack / no resolvable frames => false (we can't prove it is foreign, so
   * we treat it as ours and let it surface — better than silently swallowing a real bug).
   */
  private static _isNonSafariStack(stack: string, ownHost: string): boolean {
    if (!stack) {
      return false;
    }
    const frameUrls = stack.match(/(?:https?|chrome-extension|moz-extension|safari-web-extension):\/\/[^\s)'"]+/gi) ?? [];
    if (frameUrls.length === 0) {
      return false;
    }
    const hasOwnFrame = frameUrls.some(frameUrl => {
      try {
        return new URL(frameUrl).host === ownHost;
      } catch {
        return false;
      }
    });
    return !hasOwnFrame;
  }

  /**
   * If this error is a pdf.js failure that should be swallowed (logged as an Information breadcrumb, not
   * routed to the error page), returns the breadcrumb title to log it under; otherwise null. Titles are
   * kept SEPARATE so each family stays independently countable in elmah across the pdf.js major bump:
   *  - render cancellation (RenderingCancelledException / "startCleanup: Page N ...") — pdf.js-specific
   *    message shapes, matched on message alone.
   *  - worker/transport teardown ("Transport destroyed" / "Worker was destroyed ...") — generic English
   *    phrases, so ALSO require a pdf.js viewer/worker frame in the stack; otherwise an unrelated error
   *    that merely embeds the phrase (e.g. a SignalR transport wrapper) would be wrongly suppressed,
   *    which the non-Safari rule above deliberately avoids ("err on the side of NOT suppressing").
   *  - any other error thrown directly FROM the vendored pdf.js bundle (its top/throwing frame is a
   *    viewer/worker file) — a genuine third-party viewer bug. Gated on the TOP frame, not mere
   *    presence, so our own integration code throwing while it calls into the viewer still surfaces.
   * Checks the thrown error's name plus every message representation we might get — zone.js wraps
   * unhandled rejections, so the original name/message can be buried inside a composed message string.
   */
  private _pdfJsSuppressionTitle(thrownError: any, errorObject: ErrorObject): string | null {
    // Rejection name FIRST, for the same reason as the stack below. zone.js wraps an unhandled rejection
    // in a real Error (`new Error('Uncaught (in promise): ' + ...)`, zone.js:2023, which then hangs the
    // original on `.rejection`), so the wrapper's own `name` is ALWAYS 'Error' — never null. Reading it
    // first would therefore make the rejection fallback unreachable for exactly the wrapped case it was
    // added for: `??` only falls through on null/undefined, and 'Error' is neither.
    const name = (thrownError?.rejection?.name ?? thrownError?.name ?? '') as string;
    const message = ErrorHandlerService._composeErrorMessages(thrownError, errorObject);
    // Rejection stack FIRST. zone.js wraps an unhandled rejection in its own Error, so jsStack holds the
    // wrapper's zone.js frames, not the viewer's. On V8 the original frames tag along only because V8
    // prefixes .stack with the message zone.js composed; Firefox and Safari omit that header, so there the
    // viewer frames are absent entirely and the top frame resolves to resolvePromise@.../zone.js — which
    // fails the frame-gated rules below. Preferring the rejection's own stack closes that gap and makes the
    // V8 path intentional rather than reliant on the embedded-message coincidence.
    const stack = ErrorHandlerService._bestStack(thrownError, errorObject);
    if (ErrorHandlerService._isPdfJsRenderCancellation(name, message)) {
      return 'Suppressed pdf.js render-cancellation error';
    }
    if (ErrorHandlerService._isPdfJsWorkerTeardown(message) && ErrorHandlerService._hasPdfJsViewerFrame(stack)) {
      return 'Suppressed pdf.js worker/transport teardown error';
    }
    if (ErrorHandlerService._isThrownFromPdfJsBundle(stack)) {
      return 'Suppressed pdf.js viewer error';
    }
    return null;
  }

  /**
   * Every message representation an error might carry, newline-joined. zone.js wraps unhandled rejections,
   * so the original message can sit on the wrapper, on `.rejection`, or only in the composed string the
   * parser produced. Shared by every message-based detector below so that widening the set (adding
   * `thrownError?.error?.message`, say) lands on all of them instead of whichever one is being edited.
   */
  private static _composeErrorMessages(thrownError: any, errorObject: ErrorObject): string {
    return [errorObject?.message, thrownError?.message, thrownError?.rejection?.message].filter(m => m != null).join('\n');
  }

  private static _isPdfJsRenderCancellation(name: string, message: string): boolean {
    if (name == 'RenderingCancelledException') {
      return true;
    }
    if (!message) {
      return false;
    }
    return /RenderingCancelledException|Rendering cancelled, page \d+|startCleanup: Page \d+ is currently rendering/.test(message);
  }

  // Anchored to the exact pdf.js WorkerTransport teardown messages, so a message that merely embeds
  // the phrase (another vendor's "transport destroyed") doesn't qualify on text alone — the caller
  // additionally requires a viewer frame in the stack.
  private static _isPdfJsWorkerTeardown(message: string): boolean {
    if (!message) {
      return false;
    }
    return /(?:^|:\s|\n)(?:Transport destroyed|Worker was destroyed\.?|Worker was destroyed during onPassword callback)$/m.test(message);
  }

  // True when a frame from the bundled pdf.js viewer/worker is present in the stack. This is now the
  // only copy of the viewer-file pattern: the apps' pre-bootstrap index.html hooks carried a duplicate
  // for the viewer errors that never reached this handler, and both went away with the teardown fix
  // that stopped those errors being thrown in the first place.
  private static _hasPdfJsViewerFrame(stack: string): boolean {
    if (!stack) {
      return false;
    }
    return /viewer-[\d.]+(?:-es5)?(?:\.min)?\.mjs|pdf\.worker|pdf(?:\.min)?\.mjs/i.test(stack);
  }

  // True when the error was thrown directly from the vendored pdf.js viewer/worker bundle — i.e. the
  // top (throwing) stack frame is a viewer/worker file. Gating on the top frame rather than mere
  // presence keeps our own code out of it: if our integration threw while calling into the viewer, our
  // frame would be on top and the error still surfaces on the error page.
  private static _isThrownFromPdfJsBundle(stack: string): boolean {
    return ErrorHandlerService._hasPdfJsViewerFrame(ErrorHandlerService._topStackFrame(stack));
  }

  // The top-most stack frame — the site that actually threw. The leading line(s) carry the error
  // name/message and no frame URL; the first line that does is the throwing frame. Handles both V8
  // ("    at fn (url:line:col)") and Firefox/Safari ("fn@url:line:col") frame shapes.
  private static _topStackFrame(stack: string): string {
    if (!stack) {
      return '';
    }
    for (const line of stack.split('\n')) {
      const trimmed = line.trim();
      if (/^at\s/.test(trimmed) || /@.+:\/\//.test(trimmed)) {
        return trimmed;
      }
    }
    return '';
  }

  // Minified names from flexmonster 2.9.139: Y$ is the row measure, and JBa the call on the grid that dispose released. An
  // upgrade can rename both, and the error then reaches the error page again until they are updated here.
  private static _isFlexmonsterDisposedMeasure(message: string, stack: string): boolean {
    return /\bJBa\b/.test(message ?? '') && /\bY\$(?: \(|@)/.test(ErrorHandlerService._topStackFrame(stack));
  }

  /**
   * True when Angular raised NG0750 — a @defer block failed to load its lazy dependency chunk. Same
   * root cause as a ChunkLoadError (a lazy chunk that didn't fetch) but wrapped in an Angular
   * RuntimeError whose message is just "NG0750", so the ChunkLoadError message checks miss it. Zone.js
   * can wrap the rejection, so check every message representation we might receive.
   */
  private _isDeferBlockLoadError(thrownError: any, errorObject: ErrorObject): boolean {
    return ErrorHandlerService._isDeferBlockLoad(ErrorHandlerService._composeErrorMessages(thrownError, errorObject));
  }

  private static _isDeferBlockLoad(message: string): boolean {
    if (!message) {
      return false;
    }
    return /NG0750/.test(message);
  }

  /**
   * `stack` is resolved by the CALLER through the same helper its suppression rule classified on
   * (_wrapperStack or _bestStack), so the breadcrumb always reports the frames that rule actually saw.
   * `extraData` carries per-incident dimensions (counters etc.). Keep those OUT of the title: elmah
   * groups by title, so a title that varies per occurrence splits one signal across several groups.
   */
  private _logSuppressedError(title: string, errorObject: ErrorObject, url: string, stack: string, extraData: { key: string; value: string }[] = []) {
    // Keep visibility without alarming the user or hitting the error page: a low-severity breadcrumb
    // (Information), never an Error. Must not throw.
    try {
      const logger = this._injector.get(LoggerService);
      logger.LogInfoMessageToElmah(title, errorObject?.message, [
        { key: 'stack', value: String(stack ?? '').slice(0, 2000) },
        { key: 'url', value: url ?? '' },
        { key: 'uuid', value: String(errorObject?.uuid ?? '') },
        ...extraData
      ]);
    } catch {
      // Never let breadcrumb logging cascade into another error.
    }
  }

  showAndLogMessage(thrownError: any, errorObject: ErrorObject, url: string) {
    // if a user is holding the dialog open don't spam the logs in case
    // repeated messages are coming in due to change detection kicking in.
    // (this will happen when there is a binding issue such as trying to access
    // property of a null object inside HTML)
    if (this._showingError) {
      return;
    }
    // Hack alert. We currently have a way to silence errors but they will always get logged
    // We don't have a way to also tell the request "don't log it". That's something we need to
    // work on in the future if we feel there's a need for that but for now we're just hardcoding
    // against sqrl URL.
    const appConfig = this._injector.get(AppConfigurationService);
    // I m not sure if its possible for appConfig to not get injected but maybe it is if this is some
    // other error thrown during early initialization. I know we do some weird stuff with appconfigservice
    // during init so let's just check for NULL to be safe
    if (appConfig) {
      // Be extra defensive and check that the URL property exists as well before checking for includes
      if (thrownError.status == HTTP_STATUS_CODE_API_NOTFOUND && thrownError.url && thrownError.url.includes(appConfig.urls.apiRootSquirrel)) {
        return;
      }
    }

    const logger = this._injector.get(LoggerService);

    // TODO: This is a quick hack to not log cancellation errors.
    // Long-term we should probably consider either
    // a) adding some 'noLog' property to error objects, check it here and also modify file transfer service to throw that
    // b) simply not throw error from dispatchcancel message but return of(null) or something
    // Both feel too risky to add at this time so maybe something to follow up on in the backend release.

    if (thrownError.status != FileTransferError.Cancelled) {
      logger.LogError(thrownError, url, errorObject.pageContext);
    }

    if (this.isPlatformBrowser) {
      window['safari-error-state'] = {
        errorObject,
        url
      };
    }

    if (!thrownError.silent) {
      if (thrownError.status == HTTP_STATUS_CODE_API_FORBIDDEN) {
        this._showingError = false;
        this.navigateWithRetry('/403');
        return;
      } else if (thrownError.status == HTTP_STATUS_CODE_GATEWAY_TIMEOUT || thrownError.status == HTTP_STATUS_CODE_UNKNOWN_ERROR) {
        this._showingError = false;
        this.navigateWithRetry('/504');
        return;
      } else if (thrownError.status == HTTP_STATUS_CODE_API_NOTFOUND) {
        this._showingError = false;
        this.navigateWithRetry('/404');
        return;
      }
    }
    if (thrownError.status == HTTP_STATUS_CODE_API_NOTAUTHORIZED || thrownError.status == HTTP_STATUS_CODE_API_TOOMANY) {
      thrownError.silent = true;
    }
    // CHUNKERROR COMMENTS
    // By the time we get here if a chunk error happened angular will flatten it to a regular JS error
    // but there are important differences. IF the error came from the main module (the one you pointed your URL
    // to and then it failed due to lazy loading) THEN the error will be of type "Uncaught in promise" and the message
    // will contain words "ChunkLoadError"
    // IF on the other hand the error came from background module then the error will be of type "ChunkLoadError" but will
    // not contain word ChunkLoadError in message. In some way this is to our advantage - basically we don't want this error
    // shown by the error page (silent = true) if it comes from main module loading. That's because we will handle that particular
    // case in app.component.ts of the main bootstrap module.
    // On the other hand - if the error comes from background module - app.component.ts will never see it SO in this case
    // we don't want it to be silent. Instead we will throw it and the error page itself will handle it in a similar way to
    // app.component
    // For better understanding of all the pieces just search for "CHUNKERROR COMMENTS" throughout the code.
    if (ErrorHandlerService.isDynamicImportFailure(thrownError.message)) {
      thrownError.silent = true;
    }
    // Ignore flexmonster redrawtoolbar error
    if (thrownError.stack != null && typeof thrownError.stack.toString == 'function' && thrownError.stack.toString().includes('_redrawToolbar ')) {
      thrownError.silent = true;
    }

    if (!thrownError.silent) {
      if (thrownError.uiOption != null) {
        this._showErrorUi(thrownError.uiOption, errorObject, url);
        return;
      }
      if (errorObject.message.startsWith('NG0100: ExpressionChangedAfterItHasBeenCheckedError') || thrownError instanceof DevError) {
        // Only show if we are in the DEV environment
        if (appConfig.featureFlags?.devExceptions) {
          // In dev mode we want to show these errors as dialogs so that devs can see them and fix them
          this._showErrorUi(ReduxErrorUiOption.Dialog, errorObject, url);
        }
        return;
      }
      if (
        !thrownError.status ||
        (thrownError.status !== HTTP_STATUS_CODE_API_VALIDATION && thrownError.status < HTTP_STATUS_CODE_WEB_VALIDATION_BASE && thrownError.status !== HTTP_STATUS_CODE_API_CONFLICT)
      ) {
        this._showErrorUi(ReduxErrorUiOption.ErrorPage, errorObject, url);
        return;
      } else {
        this._showErrorUi(ReduxErrorUiOption.Dialog, errorObject, url);
        return;
      }
    }
  }
  private _showErrorUi(uiOption: ReduxErrorUiOption, errorObject, url) {
    if (uiOption == ReduxErrorUiOption.Dialog) {
      const initialState = {
        errorObject,
        url,
        list: [],
        title: 'Error'
      };
      // This is for validation errors and conflict errors - basically errors that shouldn't send you to the error page
      this.presentErrorDialog(initialState);
    } else {
      this._showingError = false;

      this.navigateWithRetry('/error');
    }
  }
  presentErrorDialog(parentData: any) {
    const appDialogUiObject = this._injector.get(AppDialogUiReduxObject);
    const store = this._injector.get(Store);

    store.dispatch(
      appDialogUiObject.default.actions.openModalDialog({
        payload: {
          component: ErrorDialogComponent.ClassId,
          buttons: ConfirmationDialogButton.Ok,
          okBtnName: 'OK',
          parentData
        }
      })
    );
  }
}
